import { env } from '@/lib/env';

type Action = 'admin_login' | 'admin_resend_code' | 'admin_step_up' | 'admin_forgot_password';

interface Grecaptcha {
  ready(cb: () => void): void;
  execute(siteKey: string, opts: { action: string }): Promise<string>;
}

let loader: Promise<Grecaptcha> | null = null;

const TIEMPO_MAXIMO_MS = 10_000;

/**
 * Carga el script una vez. Si falla (bloqueado, sin `grecaptcha` o sin respuesta) rechaza con un
 * mensaje y olvida la promesa, para que el siguiente intento vuelva a cargarlo; si no, el flujo
 * quedaría colgado sin error hasta recargar la página.
 */
function load(): Promise<Grecaptcha> {
  loader ??= new Promise((resolve, reject) => {
    const fallar = () => {
      clearTimeout(timer);
      loader = null;
      reject(
        new Error(
          'No se pudo cargar reCAPTCHA. Desactiva el bloqueador de contenido e inténtalo de nuevo.',
        ),
      );
    };
    const timer = setTimeout(fallar, TIEMPO_MAXIMO_MS);
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(env.recaptchaSiteKey)}`;
    script.async = true;
    script.onload = () => {
      const g = (window as unknown as { grecaptcha?: Grecaptcha }).grecaptcha;
      if (!g) return fallar();
      g.ready(() => {
        clearTimeout(timer);
        resolve(g);
      });
    };
    script.onerror = fallar;
    document.head.appendChild(script);
  });
  return loader;
}

/**
 * Token de reCAPTCHA v3 justo antes de enviar. Sin llave pública (local) devuelve undefined y la
 * API corre con RECAPTCHA_BYPASS.
 */
export async function recaptchaToken(action: Action): Promise<string | undefined> {
  if (!env.recaptchaSiteKey) return undefined;
  const g = await load();
  return conTiempoMaximo(g.execute(env.recaptchaSiteKey, { action }));
}

/** Si reCAPTCHA no responde, falla con aviso en vez de dejar el botón esperando para siempre. */
function conTiempoMaximo<T>(promesa: Promise<T>): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const limite = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error('reCAPTCHA no respondió. Recarga la página e inténtalo de nuevo.')),
      TIEMPO_MAXIMO_MS,
    );
  });
  return Promise.race([promesa, limite]).finally(() => clearTimeout(timer));
}

export const recaptchaHeader = (token: string | undefined) =>
  token ? { 'X-Recaptcha-Token': token } : {};
